import React, { startTransition, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { getLocalStorageJSON, setLocalStorageJSON } from '../../../utils/storageCache'
import QuestDetailPopover from './QuestDetailPopover'
import QuestEnergyOrb from './QuestEnergyOrb'
import QuestGrowthBar from './QuestGrowthBar'
import QuestNode from './QuestNode'
import QuestParallax from './QuestParallax'
import QuestStage from './QuestStage'
import StageBadge from './StageBadge'
import {
  buildFullPathD,
  computeQuestLayout,
  defaultCollapsedStageKeys,
  LAYOUT,
  measureNodeArcLengths,
} from './layoutMath'
import { clampScore, growthStops, seenScoreStorageKey, shouldBurst } from './scoreUtils'
import { badgeStorageKey, summarizeSection } from './stageProgress'
import { normalizeQuestStatus, resolveNodeStatus } from './questTypes'
import './QuestMap.css'

function computePopoverStyle(node, mapWidth) {
  if (!node) return null
  const cardW = 280
  const gap = 14
  const preferRight = node.x + LAYOUT.NODE_SIZE / 2 + gap + cardW < mapWidth - 12
  const left = preferRight
    ? node.x + LAYOUT.NODE_SIZE / 2 + gap
    : Math.max(8, node.x - LAYOUT.NODE_SIZE / 2 - gap - cardW)
  const top = Math.max(8, node.y - 28)
  return {
    left,
    top,
    ['--quest-popover-origin']: preferRight ? 'left' : 'right',
  }
}

function findScrollParent(el) {
  let node = el?.parentElement
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node)
    const oy = style.overflowY
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && node.scrollHeight > node.clientHeight + 8) {
      return node
    }
    node = node.parentElement
  }
  return null
}

function evaluatedScore(item) {
  const status = normalizeQuestStatus(
    item?.progress?.status,
    item?.assignmentStatus || item?.progress?.assignmentStatus,
  )
  if (status !== 'evaluated') return null
  return clampScore(item?.evaluationSummary?.score)
}

async function burstAt(host, key) {
  const el = host?.querySelector(`[data-quest-key="${CSS.escape(key)}"]`)
  if (!el) return
  const rect = el.getBoundingClientRect()
  const confetti = (await import('canvas-confetti')).default
  confetti({
    particleCount: 42,
    spread: 56,
    startVelocity: 26,
    scalar: 0.7,
    origin: {
      x: Math.min(0.98, Math.max(0.02, (rect.left + rect.width / 2) / window.innerWidth)),
      y: Math.min(0.98, Math.max(0.02, (rect.top + rect.height / 2) / window.innerHeight)),
    },
  })
}

export default function QuestMap({
  sections,
  timelineUnit,
  courseId,
  courseName,
  selectedKey,
  onSelectItem,
  onCloseDetail,
  onLaunch,
  onOpenHomework,
  onOpenEvaluation,
  onOpenGrowth,
}) {
  const hostRef = useRef(null)
  const sceneRef = useRef(null)
  const measureRef = useRef(null)
  const farRef = useRef(null)
  const midRef = useRef(null)
  const nearRef = useRef(null)
  const didAutoScrollRef = useRef(false)
  const rideTokenRef = useRef('')
  const rideTimerRef = useRef(0)
  const celebratedSigRef = useRef('')
  const burstDoneRef = useRef(new Set())
  const [width, setWidth] = useState(640)
  const [showJump, setShowJump] = useState(false)
  const [arcLengths, setArcLengths] = useState([])
  const [drawn, setDrawn] = useState(0)
  const [seenCourseId, setSeenCourseId] = useState(courseId)
  const [seen, setSeen] = useState(() => getLocalStorageJSON(seenScoreStorageKey(courseId), {}) || {})
  const [revealed, setRevealed] = useState(() => new Set())
  const [orbTravel, setOrbTravel] = useState(false)
  const [pulseKeys, setPulseKeys] = useState(() => new Set())
  const [freshBadges, setFreshBadges] = useState(() => new Set())
  const [badgeSeen, setBadgeSeen] = useState(() => getLocalStorageJSON(badgeStorageKey(courseId), {}) || {})
  const reduceMotion = useReducedMotion()
  const viewTimeline = typeof CSS !== 'undefined' && CSS.supports?.('animation-timeline', 'view()')
  const gid = useId().replace(/:/g, '') || 'q'
  const [collapsedKeys, setCollapsedKeys] = useState(() => defaultCollapsedStageKeys(sections))

  if (seenCourseId !== courseId) {
    setSeenCourseId(courseId)
    setSeen(getLocalStorageJSON(seenScoreStorageKey(courseId), {}) || {})
    setRevealed(new Set())
    setBadgeSeen(getLocalStorageJSON(badgeStorageKey(courseId), {}) || {})
    setFreshBadges(new Set())
    setPulseKeys(new Set())
    setOrbTravel(false)
    rideTokenRef.current = ''
    celebratedSigRef.current = ''
    burstDoneRef.current = new Set()
  }

  useEffect(() => {
    setCollapsedKeys(defaultCollapsedStageKeys(sections))
    didAutoScrollRef.current = false
  }, [sections])

  useEffect(() => {
    const el = hostRef.current
    if (!el) return undefined
    const update = () => {
      const next = el.clientWidth || 640
      setWidth((prev) => (Math.abs(next - prev) > 2 ? next : prev))
    }
    update()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (!selectedKey) return
    ;(sections || []).forEach((section, stageIndex) => {
      const stageKey = `${section.no ?? 'x'}-${stageIndex}`
      const hit = (section.items || []).some((item) => item.key === selectedKey)
      if (hit) {
        setCollapsedKeys((prev) => {
          if (!prev.has(stageKey)) return prev
          const next = new Set(prev)
          next.delete(stageKey)
          return next
        })
      }
    })
  }, [selectedKey, sections])

  const layout = useMemo(
    () => computeQuestLayout(sections, width, { collapsedStageKeys: collapsedKeys }),
    [sections, width, collapsedKeys],
  )

  const pathD = useMemo(() => buildFullPathD(layout.nodes), [layout.nodes])

  useLayoutEffect(() => {
    const next = measureNodeArcLengths(measureRef.current, layout.nodes)
    setArcLengths((prev) => {
      if (prev.length === next.length && prev.every((value, index) => Math.abs(value - next[index]) < 0.5)) {
        return prev
      }
      return next
    })
  }, [pathD, layout.nodes, layout.width, layout.height])

  const freshKeys = useMemo(() => {
    const keys = new Set()
    layout.nodes.forEach((node) => {
      const score = evaluatedScore(node.item)
      if (score == null) return
      const prev = seen?.[node.key]
      if (prev == null || Math.abs(Number(prev) - score) >= 0.05) keys.add(node.key)
    })
    return keys
  }, [layout.nodes, seen])

  const freshSig = useMemo(() => [...freshKeys].join('|'), [freshKeys])
  const activeNode = layout.activeNode
  const restIndex = useMemo(() => {
    const active = layout.nodes.findIndex((node) => node.isActive)
    if (active >= 0) return active
    let lastEvaluated = -1
    layout.nodes.forEach((node, index) => {
      if (evaluatedScore(node.item) != null) lastEvaluated = index
    })
    if (lastEvaluated >= 0) return lastEvaluated
    return Math.max(0, layout.nodes.length - 1)
  }, [layout.nodes])

  const progressLength = arcLengths[restIndex] || 0
  const pathTotal = arcLengths.length ? arcLengths[arcLengths.length - 1] || 0 : 0

  useEffect(() => {
    if (reduceMotion) {
      setDrawn(progressLength)
      return undefined
    }
    const start = performance.now()
    let frame = 0
    const tick = (now) => {
      const t = Math.min(1, (now - start) / 800)
      const eased = 1 - (1 - t) ** 3
      setDrawn(progressLength * eased)
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [progressLength, reduceMotion])

  const persistSeen = useCallback(() => {
    const next = { ...(seen || {}) }
    layout.nodes.forEach((node) => {
      const score = evaluatedScore(node.item)
      if (score != null) next[node.key] = score
    })
    startTransition(() => {
      setLocalStorageJSON(seenScoreStorageKey(courseId), next)
    })
  }, [courseId, layout.nodes, seen])

  const freshKeysRef = useRef(freshKeys)
  freshKeysRef.current = freshKeys
  const nodesRef = useRef(layout.nodes)
  nodesRef.current = layout.nodes
  const persistRef = useRef(persistSeen)
  persistRef.current = persistSeen

  useEffect(() => {
    if (!layout.nodes.length) return undefined
    const token = `${courseId}|${layout.nodes.map((node) => node.key).join(',')}|${freshSig}|${reduceMotion ? 1 : 0}`
    if (rideTokenRef.current === token) return undefined
    rideTokenRef.current = token
    window.clearTimeout(rideTimerRef.current)

    if (!freshKeys.size || celebratedSigRef.current === freshSig) return undefined
    if (reduceMotion) {
      celebratedSigRef.current = freshSig
      persistRef.current()
      return undefined
    }
    setPulseKeys(new Set(freshKeys))
    const timers = [
      window.setTimeout(() => setRevealed(new Set(freshKeysRef.current)), 280),
      window.setTimeout(() => setOrbTravel(true), 700),
      window.setTimeout(() => {
        const keys = freshKeysRef.current
        const burstNode = [...nodesRef.current].reverse().find((node) => {
          if (!keys.has(node.key)) return false
          return shouldBurst(evaluatedScore(node.item), node.item?.evaluationSummary?.deltaFromPrevious)
        })
        if (burstNode && !burstDoneRef.current.has(burstNode.key)) {
          burstDoneRef.current.add(burstNode.key)
          burstAt(hostRef.current, burstNode.key)
        }
        celebratedSigRef.current = [...keys].join('|')
        persistRef.current()
        setOrbTravel(false)
        setPulseKeys(new Set())
      }, 1600),
    ]
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [courseId, freshKeys, freshSig, layout.nodes, reduceMotion])

  useEffect(() => () => window.clearTimeout(rideTimerRef.current), [])

  const stops = useMemo(() => growthStops(
    layout.nodes
      .map((node) => ({ y: node.y, score: evaluatedScore(node.item) }))
      .filter((point) => point.score != null),
    layout.height,
  ), [layout.nodes, layout.height])

  const scrollToActive = (behavior) => {
    const node = activeNode
    const host = hostRef.current
    if (!node || !host) return
    const scroller = findScrollParent(host)
    if (!scroller) {
      host.querySelector(`[data-quest-key="${node.key}"]`)?.scrollIntoView({
        behavior,
        block: 'center',
      })
      return
    }
    const hostTop = host.offsetTop
    const target = Math.max(0, hostTop + node.y - scroller.clientHeight * 0.35)
    scroller.scrollTo({ top: target, behavior })
  }

  useEffect(() => {
    if (!activeNode || didAutoScrollRef.current) return undefined
    const timer = window.setTimeout(() => {
      scrollToActive(reduceMotion ? 'auto' : 'smooth')
      didAutoScrollRef.current = true
    }, 80)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNode?.key, reduceMotion, layout.height])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return undefined
    const scroller = findScrollParent(host)
    if (!scroller) return undefined

    const applyParallax = (relative) => {
      if (reduceMotion || viewTimeline) return
      const shift = (layer, factor) => {
        if (layer) layer.style.transform = `translate3d(0, ${relative * factor}px, 0)`
      }
      shift(farRef.current, 0.12)
      shift(midRef.current, 0.28)
      shift(nearRef.current, 0.46)
    }

    const onScroll = () => {
      const hostTop = host.offsetTop
      const relative = Math.max(0, scroller.scrollTop - hostTop)
      applyParallax(relative)
      if (!activeNode) {
        setShowJump(false)
        return
      }
      const nodeTop = hostTop + activeNode.y - 48
      const nodeBottom = nodeTop + 120
      const viewTop = scroller.scrollTop
      const viewBottom = viewTop + scroller.clientHeight
      const inView = nodeBottom > viewTop + 8 && nodeTop < viewBottom - 8
      setShowJump(!inView)
    }
    onScroll()
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', onScroll)
  }, [activeNode, layout.height, reduceMotion, viewTimeline])

  const hasSelection = Boolean(selectedKey)

  const toggleCollapse = (stageKey) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(stageKey)) next.delete(stageKey)
      else next.add(stageKey)
      return next
    })
  }

  const selectedNode = useMemo(
    () => (layout.nodes || []).find((n) => n.key === selectedKey) || null,
    [layout.nodes, selectedKey],
  )
  const selectedItem = selectedNode?.item || null
  const popoverStyle = useMemo(
    () => computePopoverStyle(selectedNode, layout.width),
    [selectedNode, layout.width],
  )
  const celebrateSelected = Boolean(selectedItem && freshKeys.has(selectedItem.key) && (revealed.has(selectedItem.key) || reduceMotion))
  const stageSummaries = useMemo(
    () => (sections || []).map((section, index) => summarizeSection(section, index)),
    [sections],
  )
  const earnedBadges = stageSummaries.filter((item) => item.earned)
  const badgeText = stageSummaries.length ? `${earnedBadges.length}/${stageSummaries.length} 枚` : ''
  const currentStage = stageSummaries[activeNode?.stageIndex] || stageSummaries[0]
  const stageText = currentStage?.title || ''
  const orbIndex = Math.max(0, restIndex)
  const orbRatio = pathTotal > 0 ? Math.max(0, ((arcLengths[orbIndex] || 0) - 36) / pathTotal) : 0
  const fromRatio = pathTotal > 0 && orbIndex > 0
    ? Math.max(0, ((arcLengths[orbIndex - 1] || 0) - 36) / pathTotal)
    : 0
  const sparkles = useMemo(() => layout.nodes.slice(0, -1).map((node, index) => {
    const next = layout.nodes[index + 1]
    return {
      key: `${node.key}-spark`,
      x: (node.x + next.x) / 2 + (index % 2 === 0 ? 16 : -16),
      y: (node.y + next.y) / 2,
    }
  }).slice(0, 8), [layout.nodes])

  useEffect(() => {
    const next = { ...(badgeSeen || {}) }
    let changed = false
    const fresh = []
    stageSummaries.forEach((summary) => {
      if (!summary.earned) return
      if (next[summary.stageKey] === summary.tier) return
      fresh.push(summary.stageKey)
      next[summary.stageKey] = summary.tier
      changed = true
    })
    if (!changed) return
    setFreshBadges(new Set(fresh))
    setBadgeSeen(next)
    startTransition(() => {
      setLocalStorageJSON(badgeStorageKey(courseId), next)
    })
  }, [stageSummaries, courseId]) // eslint-disable-line react-hooks/exhaustive-deps

  const jumpToStage = (stage) => {
    const host = hostRef.current
    if (!host || !stage) return
    const scroller = findScrollParent(host)
    const top = stage.entry?.y || stage.bannerTop || 0
    if (!scroller) return
    scroller.scrollTo({ top: Math.max(0, host.offsetTop + top - 24), behavior: reduceMotion ? 'auto' : 'smooth' })
  }

  return (
    <div
      ref={hostRef}
      className={`quest-map${hasSelection ? ' has-selection' : ''}${viewTimeline ? ' supports-view-timeline' : ''}`}
      role="region"
      aria-label="学习路径地图"
    >
      <QuestGrowthBar
        nodes={layout.nodes}
        stageText={stageText}
        badgeText={badgeText ? `徽章 ${badgeText}` : ''}
        onOpen={onOpenGrowth ? () => onOpenGrowth({ courseId, courseName }) : null}
      />
      <div
        ref={sceneRef}
        className="quest-map-scene"
        style={{ height: layout.height, width: '100%' }}
      >
        <QuestParallax
          height={layout.height}
          stages={layout.stages}
          farRef={farRef}
          midRef={midRef}
          nearRef={nearRef}
        />
        <svg
          className="quest-map-svg"
          width="100%"
          height={layout.height}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          preserveAspectRatio="xMidYMin meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id={`quest-growth-${gid}`}
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2="0"
              y2={layout.height}
            >
              {(stops.length ? stops : [{ offset: 0, color: '#6f9fff' }, { offset: 1, color: '#34d399' }]).map((stop) => (
                <stop
                  key={`${stop.offset}-${stop.color}`}
                  offset={`${stop.offset * 100}%`}
                  stopColor={stop.color}
                />
              ))}
            </linearGradient>
          </defs>

          {pathD ? (
            <g>
              <path
                className="quest-path-snake-shadow"
                d={pathD}
                fill="none"
                strokeWidth="10"
                strokeLinecap="round"
                transform="translate(0 3)"
              />
              <path
                ref={measureRef}
                d={pathD}
                fill="none"
                stroke="none"
              />
              <path
                className={`quest-path-pending${reduceMotion ? '' : ' is-ants'}`}
                d={pathD}
                fill="none"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                className={`quest-path-walked${reduceMotion ? '' : ' is-flow'}`}
                d={pathD}
                fill="none"
                stroke={`url(#quest-growth-${gid})`}
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={`${Math.max(0, drawn)} ${Math.max(pathTotal, 1)}`}
              />
              {!reduceMotion ? (
                <path
                  className="quest-path-pulse"
                  d={pathD}
                  fill="none"
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`18 ${Math.max(pathTotal, 80)}`}
                />
              ) : null}
              {sparkles.map((spark) => (
                <circle key={spark.key} className="quest-path-spark" cx={spark.x} cy={spark.y} r="4.5" />
              ))}
            </g>
          ) : null}
        </svg>

        {layout.stages.map((stage) => (
          <QuestStage
            key={`stage-${stage.stageKey}`}
            stage={stage}
            timelineUnit={timelineUnit}
            progress={stageSummaries[stage.stageIndex]}
            onToggleCollapse={toggleCollapse}
            onJump={jumpToStage}
          />
        ))}

        {layout.nodes.map((node, index) => {
          const status = resolveNodeStatus(node.item, selectedKey, {
            dimOthers: hasSelection,
          })
          const pos = {
            left: node.x - LAYOUT.NODE_SIZE / 2,
            top: node.y - LAYOUT.NODE_SIZE / 2,
          }
          const labelSide = node.labelSide || 'below'
          const isFresh = freshKeys.has(node.key)

          const handleClick = () => {
            if (node.hiddenCount > 0) toggleCollapse(node.stageKey)
            onSelectItem?.(node.item)
          }

          const nodeProps = {
            item: node.item,
            status,
            onClick: handleClick,
            hiddenCount: node.hiddenCount || 0,
            labelSide,
            celebrate: isFresh && !reduceMotion,
            milestone: node.isMilestone,
            pulse: pulseKeys.has(node.key),
          }

          if (reduceMotion) {
            return <QuestNode key={node.key} {...nodeProps} style={pos} />
          }

          return (
            <motion.div
              key={node.key}
              className="quest-node-motion-wrap"
              style={{
                position: 'absolute',
                left: pos.left,
                top: pos.top,
              }}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.2 }}
            >
              <QuestNode
                {...nodeProps}
                style={{ position: 'relative', left: 0, top: 0 }}
              />
            </motion.div>
          )
        })}

        {layout.stages.map((stage) => {
          const summary = stageSummaries[stage.stageIndex]
          const anchor = layout.nodes.find((node) => node.isMilestone && node.stageKey === stage.stageKey)
          if (!summary || !anchor) return null
          const outward = anchor.x < layout.centerX ? -78 : 78
          return (
            <StageBadge
              key={`badge-${stage.stageKey}`}
              summary={summary}
              theme={stage.theme}
              celebrate={freshBadges.has(summary.stageKey)}
              unitLabel={stage.title || `阶段${stage.stageIndex + 1}`}
              style={{ left: anchor.x + outward - 18, top: anchor.y - 18 }}
            />
          )
        })}

        {activeNode ? (
          <>
            <div className="quest-fog" style={{ top: activeNode.y + 56 }} />
            {layout.nodes[layout.nodes.length - 1]?.key === activeNode.key ? (
              <div
                className="quest-next-gate"
                style={{ left: activeNode.x - 36, top: activeNode.y + 78 }}
              >
                <span className="quest-next-gate-road" />
                <span className="quest-next-gate-label">本课终点</span>
              </div>
            ) : null}
          </>
        ) : null}

        {layout.nodes.length ? (
          <QuestEnergyOrb
            pathD={pathD}
            ratio={orbRatio}
            fromRatio={fromRatio}
            travel={orbTravel && !reduceMotion}
            reduceMotion={Boolean(reduceMotion)}
            point={layout.nodes[orbIndex]}
          />
        ) : null}

        <QuestDetailPopover
          open={Boolean(selectedItem && popoverStyle)}
          item={selectedItem}
          style={popoverStyle || undefined}
          onClose={onCloseDetail}
          onLaunch={onLaunch}
          onOpenHomework={onOpenHomework}
          onOpenEvaluation={onOpenEvaluation}
          celebrateScore={celebrateSelected}
        />
      </div>

      {showJump && activeNode ? (
        <button
          type="button"
          className="quest-jump-here"
          onClick={() => scrollToActive(reduceMotion ? 'auto' : 'smooth')}
        >
          当前应做
        </button>
      ) : null}
    </div>
  )
}
